import React, { useState, useEffect } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import { sanitizeForDisplay } from '../../utils/htmlSanitizer';

/**
 * EvaluationQuestionEditorModal Component
 * React implementation that matches the behavior of the original modal
 * Allows editing evaluation questions for topics
 */
const EvaluationQuestionEditorModal = ({ show, onHide, topicId, moduleSemesterId, topicName }) => {
  // State management
  const [evaluationStats, setEvaluationStats] = useState([]);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Question management
  const [existingQuestions, setExistingQuestions] = useState([]);
  const [evaluationId, setEvaluationId] = useState(null);
  const [randomAddMode, setRandomAddMode] = useState(true);
  const [questionGenerator, setQuestionGenerator] = useState({
    numberOfQuestions: 0,
    numberOfBeginnerQuestions: 0,
    numberOfAdvBeginnerQuestions: 0,
    numberOfCompetentQuestions: 0,
    numberOfExpertQuestions: 0
  });
  const [questionForSelection, setQuestionForSelection] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState(1);
  const [questionIDtoLoad, setQuestionIDtoLoad] = useState('');

  // Load evaluation statistics when modal is shown
  useEffect(() => {
    if (show && topicId && moduleSemesterId) {
      // Use pre-loaded data from ScaffoldManagementModal if available
      if (window.evaluationQuestionStats) {
        loadEvaluationStatisticsFromPreloaded();
      } else {
        loadEvaluationStatistics();
      }
      
      if (window.evaluationData) {
        loadExistingEvaluationFromPreloaded();
      } else {
        loadExistingEvaluation();
      }
    }
  }, [show, topicId, moduleSemesterId]);

  /**
   * Load evaluation statistics from pre-loaded data
   */
  const loadEvaluationStatisticsFromPreloaded = () => {
    try {
      const data = window.evaluationQuestionStats;
      console.log('Using pre-loaded evaluation statistics:', data);
      console.log('Data type:', typeof data);
      console.log('Data structure:', JSON.stringify(data, null, 2));

      // Process the statistics data - handle different possible response structures
      let stats = [];
      
      // Handle different possible response structures
      if (data) {
        if (data.d) {
          // Response wrapped in .d property
          stats = Array.isArray(data.d) ? data.d : [data.d];
        } else if (Array.isArray(data)) {
          // Direct array response
          stats = data;
        } else if (data.Questions) {
          // Response with Questions property
          stats = data.Questions;
        } else if (data.Statistics) {
          // Response with Statistics property
          stats = data.Statistics;
        } else {
          // Single object response
          stats = [data];
        }
      }

      console.log('Processed stats array:', stats);

      // Ensure we have statistics for all difficulty levels (1-4)
      const completeStats = [];
      for (let difficulty = 1; difficulty <= 4; difficulty++) {
        const existingStat = stats.find(stat => 
          stat.Difficulty === difficulty || 
          stat.difficulty === difficulty ||
          stat.Level === difficulty ||
          stat.level === difficulty
        );
        
        completeStats.push({
          Difficulty: difficulty,
          NumberInTotal: existingStat ? (existingStat.NumberInTotal || existingStat.numberInTotal || existingStat.Count || existingStat.count || 0) : 0,
          NumberOfFinalized: existingStat ? (existingStat.NumberOfFinalized || existingStat.numberOfFinalized || existingStat.Finalized || existingStat.finalized || 0) : 0
        });
      }

      console.log('Complete stats for UI:', completeStats);
      setEvaluationStats(completeStats);
      
      // Clear the global variable after use
      window.evaluationQuestionStats = null;

    } catch (err) {
      console.error('Error loading pre-loaded evaluation statistics:', err);
      setError(err.message);
    }
  };

  /**
   * Load existing evaluation from pre-loaded data
   */
  const loadExistingEvaluationFromPreloaded = () => {
    try {
      const data = window.evaluationData;
      console.log('Using pre-loaded evaluation data:', data);
      console.log('Evaluation data type:', typeof data);
      console.log('Evaluation data structure:', JSON.stringify(data, null, 2));

      if (data) {
        // Handle different possible response structures
        let questions = [];
        let evaluationId = null;
        let duration = 0;

        // Extract questions
        if (data.Questions) {
          questions = Array.isArray(data.Questions) ? data.Questions : [data.Questions];
        } else if (data.questions) {
          questions = Array.isArray(data.questions) ? data.questions : [data.questions];
        } else if (Array.isArray(data)) {
          questions = data;
        }

        // Extract evaluation ID
        evaluationId = data.EvaluationId || data.evaluationId || data.ID || data.id || null;

        // Extract duration
        duration = data.DurationInMinutes || data.durationInMinutes || data.Duration || data.duration || 0;

        console.log('Extracted questions:', questions);
        console.log('Extracted evaluation ID:', evaluationId);
        console.log('Extracted duration:', duration);

        setExistingQuestions(questions);
        setEvaluationId(evaluationId);
        setDuration(parseInt(duration));
      }
      
      // Clear the global variable after use
      window.evaluationData = null;

    } catch (err) {
      console.error('Error loading pre-loaded evaluation data:', err);
      // Don't set error here as evaluation might not exist yet
    }
  };

  /**
   * Load evaluation statistics for the topic
   */
  const loadEvaluationStatistics = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      // Get evaluation statistics for the topic
      const response = await fetch('./webservices/Question.svc/countAllQ', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          topicId: topicId
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load evaluation statistics');
      }

      const data = await response.json();
      console.log('Evaluation statistics received:', data);
      console.log('Statistics data type:', typeof data);
      console.log('Statistics data structure:', JSON.stringify(data, null, 2));

      // Process the statistics data - handle different possible response structures
      let stats = [];
      
      // Handle different possible response structures
      if (data) {
        if (data.d) {
          // Response wrapped in .d property
          stats = Array.isArray(data.d) ? data.d : [data.d];
        } else if (Array.isArray(data)) {
          // Direct array response
          stats = data;
        } else if (data.Questions) {
          // Response with Questions property
          stats = data.Questions;
        } else if (data.Statistics) {
          // Response with Statistics property
          stats = data.Statistics;
        } else {
          // Single object response
          stats = [data];
        }
      }

      console.log('Processed stats array:', stats);

      // Ensure we have statistics for all difficulty levels (1-4)
      const completeStats = [];
      for (let difficulty = 1; difficulty <= 4; difficulty++) {
        const existingStat = stats.find(stat => 
          stat.Difficulty === difficulty || 
          stat.difficulty === difficulty ||
          stat.Level === difficulty ||
          stat.level === difficulty
        );
        
        completeStats.push({
          Difficulty: difficulty,
          NumberInTotal: existingStat ? (existingStat.NumberInTotal || existingStat.numberInTotal || existingStat.Count || existingStat.count || 0) : 0,
          NumberOfFinalized: existingStat ? (existingStat.NumberOfFinalized || existingStat.numberOfFinalized || existingStat.Finalized || existingStat.finalized || 0) : 0
        });
      }

      console.log('Complete stats for UI:', completeStats);
      setEvaluationStats(completeStats);

    } catch (err) {
      console.error('Error loading evaluation statistics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load existing evaluation data
   */
  const loadExistingEvaluation = async () => {
    try {
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      const response = await fetch('./webservices/Evaluation.svc/GetEvaluation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          topicId: topicId,
          msId: moduleSemesterId
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load existing evaluation');
      }

      const data = await response.json();
      console.log('Existing evaluation data:', data);
      console.log('Evaluation data type:', typeof data);
      console.log('Evaluation data structure:', JSON.stringify(data, null, 2));

      if (data) {
        // Handle different possible response structures
        let questions = [];
        let evaluationId = null;
        let duration = 0;

        // Extract questions
        if (data.Questions) {
          questions = Array.isArray(data.Questions) ? data.Questions : [data.Questions];
        } else if (data.questions) {
          questions = Array.isArray(data.questions) ? data.questions : [data.questions];
        } else if (Array.isArray(data)) {
          questions = data;
        }

        // Extract evaluation ID
        evaluationId = data.EvaluationId || data.evaluationId || data.ID || data.id || null;

        // Extract duration
        duration = data.DurationInMinutes || data.durationInMinutes || data.Duration || data.duration || 0;

        console.log('Extracted questions:', questions);
        console.log('Extracted evaluation ID:', evaluationId);
        console.log('Extracted duration:', duration);

        setExistingQuestions(questions);
        setEvaluationId(evaluationId);
        setDuration(parseInt(duration));
      }

    } catch (err) {
      console.error('Error loading existing evaluation:', err);
      // Don't set error here as evaluation might not exist yet
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    setError(null);
    if (onHide) {
      onHide();
    }
  };

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  /**
   * Handle save evaluation settings
   */
  const handleSave = async () => {
    try {
      console.log('Saving evaluation settings...');
      
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      // Save evaluation questions
      const questionIds = existingQuestions.map(q => q.QuestionId || q.ID);
      
      const questionsResponse = await fetch('./webservices/Evaluation.svc/AddEvaluationQuestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          questionIds: questionIds,
          topicId: topicId,
          msId: moduleSemesterId
        }),
        credentials: 'include',
      });

      if (!questionsResponse.ok) {
        throw new Error('Failed to save evaluation questions');
      }

      // Save evaluation duration
      if (evaluationId) {
        const durationResponse = await fetch('./webservices/Evaluation.svc/UpdateEvaluationDuration', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: encodedToken,
            evaluationId: evaluationId,
            durationInSeconds: duration * 60
          }),
          credentials: 'include',
        });

        if (!durationResponse.ok) {
          throw new Error('Failed to save evaluation duration');
        }
      }

      console.log('Evaluation settings saved successfully');
      handleClose();
      
    } catch (error) {
      console.error('Error saving evaluation settings:', error);
      alert('Failed to save evaluation settings');
    }
  };

  /**
   * Get difficulty text
   */
  const getDifficultyText = (difficulty) => {
    switch (parseInt(difficulty)) {
      case 1: return 'Beginner';
      case 2: return 'Adv. Beginner';
      case 3: return 'Competent';
      case 4: return 'Expert';
      default: return 'Unknown';
    }
  };

  /**
   * Update all level question numbers
   */
  const updateAllLevelQuestionNumber = (number) => {
    setQuestionGenerator(prev => ({
      ...prev,
      numberOfBeginnerQuestions: number,
      numberOfAdvBeginnerQuestions: number,
      numberOfCompetentQuestions: number,
      numberOfExpertQuestions: number,
      numberOfQuestions: number * 4
    }));
  };

  /**
   * Generate questions randomly
   */
  const generateQuestions = async () => {
    try {
      console.log('Generating questions with settings:', questionGenerator);
      
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      const questionDistribution = [
        questionGenerator.numberOfBeginnerQuestions,
        questionGenerator.numberOfAdvBeginnerQuestions,
        questionGenerator.numberOfCompetentQuestions,
        questionGenerator.numberOfExpertQuestions
      ];

      // Pass existing questions to avoid duplicates
      const existingQuestionIds = existingQuestions.map(q => q.QuestionId || q.ID);

      const response = await fetch('./webservices/Exam.svc/GenerateQuestionsByDifficulty', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          questionDistribution: questionDistribution,
          topicId: topicId,
          existingQuestionIds: existingQuestionIds,
          onlyTemplate: false
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to generate questions');
      }

      const data = await response.json();
      console.log('Generated questions:', data);
      console.log('Generated questions type:', typeof data);
      console.log('Generated questions structure:', JSON.stringify(data, null, 2));

      // Handle different possible response structures
      let questions = [];
      
      if (data) {
        if (Array.isArray(data)) {
          // Direct array response (as shown in your example)
          questions = data;
        } else if (data.Questions) {
          // Response wrapped in Questions property
          questions = Array.isArray(data.Questions) ? data.Questions : [data.Questions];
        } else if (data.questions) {
          // Response wrapped in questions property (lowercase)
          questions = Array.isArray(data.questions) ? data.questions : [data.questions];
        } else if (data.d) {
          // Response wrapped in .d property
          questions = Array.isArray(data.d) ? data.d : [data.d];
        }
      }

      console.log('Extracted questions array:', questions);
      console.log('Number of questions found:', questions.length);

      if (questions.length > 0) {
        setExistingQuestions(prev => [...prev, ...questions]);
        alert(`Generated ${questions.length} questions successfully`);
      } else {
        alert('No questions were generated');
      }

    } catch (error) {
      console.error('Error generating questions:', error);
      alert('Failed to generate questions');
    }
  };

  /**
   * Add question by ID to quiz
   */
  const addQuestionIDToQuiz = async (questionId) => {
    try {
      if (!questionId) {
        alert('Please enter a question ID');
        return;
      }
      
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      // Get question info by ID
      const response = await fetch(`./webservices/Question.svc/Basic?token=${encodedToken}&questionId=${questionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load question by ID');
      }

      const data = await response.json();
      console.log('Basic API response:', data);
      console.log('Basic API response type:', typeof data);
      console.log('Basic API response structure:', JSON.stringify(data, null, 2));

      // Handle different possible response structures
      let question = null;
      
      if (data) {
        if (data.Question) {
          question = data.Question;
        } else if (data.question) {
          question = data.question;
        } else if (data.d && data.d.Question) {
          question = data.d.Question;
        } else if (data.d && data.d.question) {
          question = data.d.question;
        } else if (data.d && !data.d.Question && !data.d.question) {
          // If data.d exists but doesn't have Question property, it might be the question itself
          question = data.d;
        } else {
          // If no .d property, the data might be the question itself
          question = data;
        }
      }

      console.log('Extracted question:', question);

      if (question) {
        // Check if question already exists
        const questionId = question.QuestionId || question.ID || question.Question?.ID;
        const exists = existingQuestions.some(q => {
          const existingId = q.QuestionId || q.ID || q.Question?.ID;
          return existingId === questionId;
        });
        
        console.log('Question ID:', questionId);
        console.log('Question already exists:', exists);
        
        if (exists) {
          alert('Question already added to evaluation');
          return;
        }

        // Add question to existing questions
        console.log('Adding question to existing questions:', question);
        setExistingQuestions(prev => {
          const newQuestions = [...prev, question];
          console.log('Updated existing questions:', newQuestions);
          return newQuestions;
        });
        setQuestionIDtoLoad(''); // Clear the input
        alert('Question added successfully');
      } else {
        console.log('No question found in response');
        alert('Question not found');
      }

    } catch (error) {
      console.error('Error adding question by ID:', error);
      alert('Failed to add question by ID');
    }
  };

  /**
   * Load questions for selection by difficulty
   */
  const loadQuestionForSelection = async (difficulty) => {
    try {
      console.log('Loading questions for difficulty:', difficulty);
      
      const token = getTokenForRequest();
      const encodedToken = window.encodeToken(token);

      const response = await fetch('./webservices/Question.svc/listAllQ', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: encodedToken,
          topicID: topicId,
          difficulty: difficulty.toString(),
          onlyTemplate: false,
          criteria: {
            Field: "",
            Keyword: "",
            OrderType: "desc",
            PageSize: 20,
            SortBy: 0,
            StartPage: 1
          }
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load questions for selection');
      }

      const data = await response.json();
      console.log('Questions for selection API response:', data);
      console.log('Response data type:', typeof data);
      console.log('Response data structure:', JSON.stringify(data, null, 2));

      // Handle different possible response structures
      let questions = [];
      
      if (data) {
        if (data.Questions) {
          questions = Array.isArray(data.Questions) ? data.Questions : [data.Questions];
        } else if (data.questions) {
          questions = Array.isArray(data.questions) ? data.questions : [data.questions];
        } else if (data.d && data.d.Questions) {
          questions = Array.isArray(data.d.Questions) ? data.d.Questions : [data.d.Questions];
        } else if (data.d && Array.isArray(data.d)) {
          questions = data.d;
        } else if (Array.isArray(data)) {
          questions = data;
        } else if (data.d) {
          questions = Array.isArray(data.d) ? data.d : [data.d];
        }
      }

      console.log('Extracted questions array:', questions);
      console.log('Number of questions found:', questions.length);

      if (questions.length > 0) {
        // Filter out questions that are already in the evaluation
        const existingQuestionIds = existingQuestions.map(q => q.QuestionId || q.ID || q.Question?.ID);
        console.log('Existing question IDs:', existingQuestionIds);
        
        const filteredQuestions = questions.filter(q => {
          const questionId = q.QuestionId || q.ID || q.Question?.ID;
          const isNotExisting = !existingQuestionIds.includes(questionId);
          console.log(`Question ${questionId} - isNotExisting: ${isNotExisting}`);
          return isNotExisting;
        });
        
        console.log('Filtered questions for selection:', filteredQuestions);
        setQuestionForSelection(filteredQuestions);
      } else {
        console.log('No questions found in response');
        setQuestionForSelection([]);
      }

    } catch (error) {
      console.error('Error loading questions for selection:', error);
      alert('Failed to load questions for selection');
    }
  };

  /**
   * Add question to quiz
   */
  const addQuestionToQuiz = (question) => {
    console.log('Adding question to quiz:', question);
    console.log('Question ID:', question.QuestionId || question.ID || question.Question?.ID);
    
    // Check if question already exists
    const questionId = question.QuestionId || question.ID || question.Question?.ID;
    const exists = existingQuestions.some(q => {
      const existingId = q.QuestionId || q.ID || q.Question?.ID;
      return existingId === questionId;
    });
    
    console.log('Question already exists:', exists);
    console.log('Current existing questions:', existingQuestions);
    
    if (exists) {
      alert('Question already added to evaluation');
      return;
    }

    console.log('Adding question to existing questions list');
    setExistingQuestions(prev => {
      const newQuestions = [...prev, question];
      console.log('Updated existing questions:', newQuestions);
      return newQuestions;
    });
    
    console.log('Removing question from selection list');
    setQuestionForSelection(prev => {
      const filtered = prev.filter(q => {
        const qId = q.QuestionId || q.ID || q.Question?.ID;
        return qId !== questionId;
      });
      console.log('Updated selection list:', filtered);
      return filtered;
    });
  };

  /**
   * Remove question from quiz
   */
  const removeQuestionFromQuiz = (index) => {
    setExistingQuestions(prev => prev.filter((_, i) => i !== index));
  };

  if (!show) return null;

  return (
    <div className="modal fade show react-modal-fix evaluation-modal" style={{ display: 'block' }} onClick={handleBackdropClick}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          
          {/* Modal Header */}
          <div className="modal-header">
            <h4 className="modal-title">
              Edit Evaluation Question for {topicName}
            </h4>
            <button 
              type="button" 
              className="btn-close-x" 
              onClick={handleClose}
              aria-label="Close"
            >×</button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            <div className="container-fluid" style={{ maxHeight: '420px', overflowY: 'auto', overflowX: 'hidden' }}>
              
              <div className="row">
                {/* Question Stats */}
                <div className="col-sm-12">
                  <div><h4>Questions Available</h4></div>
                  
                  {loading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading evaluation statistics...</p>
                    </div>
                  ) : error ? (
                    <div className="alert alert-danger">
                      <h6>Error Loading Statistics</h6>
                      <p className="mb-0">{error}</p>
                    </div>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Beginner</th>
                          <th>Adv. Beginner</th>
                          <th>Competent</th>
                          <th>Expert</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{evaluationStats[0]?.NumberInTotal || 0}</td>
                          <td>{evaluationStats[1]?.NumberInTotal || 0}</td>
                          <td>{evaluationStats[2]?.NumberInTotal || 0}</td>
                          <td>{evaluationStats[3]?.NumberInTotal || 0}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Duration Setting */}
                <div className="col-sm-12">
                  <div className="form-group">
                    <label>Duration in Minutes</label>
                    <input
                      type="number"
                      className="form-control"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                      style={{ width: '120px' }}
                    />
                    <br />
                    <label>If "0" is set, it means that no time limit is set.</label>
                  </div>
                </div>

                {/* Existing Questions */}
                <div className="col-sm-6">
                  <div className="existing-questions-section">
                    <div className="title">
                      <h3>Existing Questions</h3>
                    </div>
                  
                  {existingQuestions.length === 0 ? (
                    <p className="text-muted">No questions added yet.</p>
                  ) : (
                    <div className="existing-questions">
                      {existingQuestions.map((question, index) => (
                        <div key={index} className="row">
                          <div className="col-sm-1">
                            {index + 1}
                          </div>
                          <div className="col-sm-9">
                            <div className="question-statement" dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(question.Question?.Statement || question.Statement || 'Question statement') }} />
                            <div className="question-level">
                              <span className="question-tag">
                                {getDifficultyText(question.Question?.Difficulty || question.Difficulty)}
                              </span>
                              <span className="question-tag">
                                {question.Question?.QuestionType || question.QuestionType || 'MCQ'}
                              </span>
                            </div>
                          </div>
                          <div className="col-sm-2">
                            <button 
                              type="button" 
                              className="btn btn-danger btn-sm"
                              onClick={() => removeQuestionFromQuiz(index)}
                              style={{ minWidth: '30px', height: '30px', padding: '0' }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                </div>

                {/* Add Questions Section */}
                <div className="col-sm-6">
                  <div className="add-questions-section">
                    {/* Add Question Mode */}
                    <div className="row">
                    <div className="col-sm-12">
                      <button 
                        className={`btn ${randomAddMode ? 'btn-success' : 'btn-default'}`}
                        onClick={() => setRandomAddMode(true)}
                      >
                        Randomly Add Question
                      </button>
                      <button 
                        className={`btn ${!randomAddMode ? 'btn-success' : 'btn-default'}`}
                        onClick={() => setRandomAddMode(false)}
                        style={{ marginLeft: '10px' }}
                      >
                        Manually Add Question
                      </button>
                    </div>
                  </div>

                  {/* Random Add Questions */}
                  {randomAddMode && (
                    <div className="generator">
                      <div className="col-sm-12" style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                        <div className="form-group">
                          <span className="title">{questionGenerator.numberOfQuestions}</span>
                          <span className="title"><label>Questions to add</label></span>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-3">
                          <label>Set for all</label>
                        </div>
                        <div className="col-sm-5">
                          <div className="form-group">
                            <select 
                              className="form-control"
                              value={questionGenerator.numberOfBeginnerQuestions}
                              onChange={(e) => updateAllLevelQuestionNumber(parseInt(e.target.value))}
                            >
                              {[0,1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-3">
                          <label>Beginner</label>
                        </div>
                        <div className="col-sm-5">
                          <div className="form-group">
                            <select 
                              className="form-control"
                              value={questionGenerator.numberOfBeginnerQuestions}
                              onChange={(e) => setQuestionGenerator(prev => ({ ...prev, numberOfBeginnerQuestions: parseInt(e.target.value) }))}
                            >
                              {[0,1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-3">
                          <label>Advanced Beginner</label>
                        </div>
                        <div className="col-sm-5">
                          <div className="form-group">
                            <select 
                              className="form-control"
                              value={questionGenerator.numberOfAdvBeginnerQuestions}
                              onChange={(e) => setQuestionGenerator(prev => ({ ...prev, numberOfAdvBeginnerQuestions: parseInt(e.target.value) }))}
                            >
                              {[0,1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-3">
                          <label>Competent</label>
                        </div>
                        <div className="col-sm-5">
                          <div className="form-group">
                            <select 
                              className="form-control"
                              value={questionGenerator.numberOfCompetentQuestions}
                              onChange={(e) => setQuestionGenerator(prev => ({ ...prev, numberOfCompetentQuestions: parseInt(e.target.value) }))}
                            >
                              {[0,1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-sm-3">
                          <label>Expert</label>
                        </div>
                        <div className="col-sm-5">
                          <div className="form-group">
                            <select 
                              className="form-control"
                              value={questionGenerator.numberOfExpertQuestions}
                              onChange={(e) => setQuestionGenerator(prev => ({ ...prev, numberOfExpertQuestions: parseInt(e.target.value) }))}
                            >
                              {[0,1,2,3,4,5,6,7,8,9,10].map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="col-sm-12">
                        <button 
                          type="button" 
                          className="btn btn-success"
                          onClick={generateQuestions}
                        >
                          Generate Question(s)
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Manual Add Questions */}
                  {!randomAddMode && (
                    <div className="generator">
                      <div className="col-sm-12" style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                        <div className="form-inline">
                          <label>Load Question by ID</label>
                          <input 
                            type="number" 
                            value={questionIDtoLoad}
                            onChange={(e) => setQuestionIDtoLoad(e.target.value)}
                            style={{ margin: '0 10px' }}
                          />
                          <button 
                            className="btn btn-success"
                            onClick={() => addQuestionIDToQuiz(questionIDtoLoad)}
                          >
                            Add Question
                          </button>
                        </div>
                      </div>

                      <div className="col-sm-12" style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '15px' }}>
                        <div className="form-inline">
                          <label>Load Question by Difficulty</label>
                          <br />
                          <select 
                            className="form-control"
                            value={selectedDifficulty}
                            onChange={(e) => setSelectedDifficulty(parseInt(e.target.value))}
                            style={{ margin: '10px 10px 10px 0' }}
                          >
                            <option value={1}>Beginner</option>
                            <option value={2}>Advanced Beginner</option>
                            <option value={3}>Competent</option>
                            <option value={4}>Expert</option>
                          </select>
                          <button 
                            type="button" 
                            className="btn btn-info"
                            onClick={() => loadQuestionForSelection(selectedDifficulty)}
                          >
                            Load Question
                          </button>
                        </div>
                      </div>

                      {/* List of questions for selection */}
                      {questionForSelection.length > 0 && (
                        <div className="col-sm-12 question-selection">
                          {questionForSelection.map((question, index) => (
                            <div key={index} className="row">
                              <div className="col-sm-8">
                                <div className="question-statement" dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(question.Question?.Statement || question.Statement || 'Question statement') }} />
                                <div className="question-level">
                                  <span className="question-tag">
                                    {getDifficultyText(question.Question?.Difficulty || question.Difficulty)}
                                  </span>
                                  <span className="question-tag">
                                    {question.Question?.QuestionType || question.QuestionType || 'MCQ'}
                                  </span>
                                </div>
                              </div>
                              <div className="col-sm-4">
                                <button 
                                  type="button" 
                                  className="btn btn-success btn-sm"
                                  onClick={() => addQuestionToQuiz(question)}
                                >
                                  Add to Evaluation
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-default" 
              onClick={handleClose}
            >
              Close
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleSave}
            >
              Save changes
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EvaluationQuestionEditorModal;
